import React, { createContext, useContext, useEffect, useState } from 'react';
import {
    onAuthStateChanged,
    signInWithPopup,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut
} from 'firebase/auth';
import { auth, googleProvider } from './firebaseClient';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [initializing, setInitializing] = useState(true);

    useEffect(() => {
        if (!auth) {
            setLoading(false);
            return;
        }

        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            console.log("🔥 Auth State Changed:", firebaseUser ? "User Logged In" : "User Logged Out", firebaseUser?.uid);

            if (firebaseUser) {
                // Define o estado inicial do usuário com dados do Firebase IMEDIATAMENTE
                setUser({
                    id: firebaseUser.uid,
                    name: firebaseUser.displayName,
                    email: firebaseUser.email,
                    photoURL: firebaseUser.photoURL,
                    isSyncing: true // Flag opcional para UI
                });

                // Libera a renderização da aplicação o mais rápido possível
                setInitializing(false);

                // Inicia sincronização com o backend em segundo plano
                try {
                    const token = await firebaseUser.getIdToken();
                    const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/google`, {}, {
                        headers: { Authorization: `Bearer ${token}` }
                    });

                    // Atualiza o usuário com os dados extras do backend após a resposta
                    setUser(prev => ({ ...prev, ...response.data.user, token, isSyncing: false }));
                } catch (error) {
                    console.error("Erro ao sincronizar usuário com backend:", error);
                    // Mantém os dados do Firebase e limpa flag de sync
                    setUser(prev => ({ ...prev, isSyncing: false }));
                }
            } else {
                setUser(null);
                setInitializing(false);
            }
            setLoading(false);
        });

        return unsubscribe;
    }, []);

    const loginWithGoogle = async () => {
        console.log("🚀 loginWithGoogle called");
        if (!auth) {
            alert("Firebase não configurado!");
            return;
        }
        try {
            await signInWithPopup(auth, googleProvider);
        } catch (error) {
            console.error("Erro no login Google:", error);
            throw error;
        }
    };

    const loginWithEmail = async (email, password) => {
        if (!auth) {
            alert("Firebase não configurado!");
            return;
        }
        try {
            await signInWithEmailAndPassword(auth, email, password);
        } catch (error) {
            console.error("Erro no login com email:", error);
            throw error;
        }
    };

    const signupWithEmail = async (email, password) => {
        if (!auth) {
            alert("Firebase não configurado!");
            return;
        }
        try {
            await createUserWithEmailAndPassword(auth, email, password);
        } catch (error) {
            console.error("Erro no cadastro:", error);
            throw error;
        }
    };

    const logout = async () => {
        if (!auth) return;
        try {
            await signOut(auth);
        } catch (error) {
            console.error("Erro no logout:", error);
        }
    };

    const updateProfile = async (data) => {
        if (!user) return;

        try {
            const token = await getFreshToken();
            if (!token) throw new Error("Não foi possível obter token de autenticação");

            const response = await axios.put(`${process.env.REACT_APP_API_URL}/auth/profile`, data, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUser({ ...user, ...response.data.user });
            return response.data;
        } catch (error) {
            console.error("Erro ao atualizar perfil:", error);
            throw error;
        }
    };

    const getFreshToken = async () => {
        if (auth.currentUser) {
            return await auth.currentUser.getIdToken();
        }
        return null;
    };

    return (
        <AuthContext.Provider value={{
            user,
            currentUser: user, // Alias for compatibility
            loading,
            initializing,
            loginWithGoogle,
            loginWithEmail,
            signupWithEmail,
            logout,
            updateProfile,
            getFreshToken // Exporta função para obter token atualizado
        }}>
            {/* Bloqueia APENAS na inicialização (primeiro check do Firebase) */}
            {!initializing && children}
        </AuthContext.Provider>
    );
};
